<template>
  <!--
    Tab "Resumen" del panel de atributos extra de una capa. Solo lectura:
    total de elementos, cuántos tienen datos importados y una agrupación por
    (pedido, línea de pedido). Se refresca al activar el tab (lazy) — ver
    LayerExtraAttributesManager.
  -->
  <div class="flex flex-col w-full">

    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-10" style="color:#7D99D4;">
      <b-icon icon="spinner" pack="fas" size="is-medium" class="fa-spin mr-2" />
      <span class="text-sm">Cargando resumen…</span>
    </div>

    <template v-else>
      <!-- Tarjetas de totales -->
      <div class="grid grid-cols-3 gap-3 mb-4">
        <div class="summary-card summary-card--total">
          <span class="summary-card__label">Total {{ elementNoun }}</span>
          <span class="summary-card__value">{{ total.toLocaleString() }}</span>
        </div>
        <div class="summary-card summary-card--with">
          <span class="summary-card__label">Con datos</span>
          <span class="summary-card__value">{{ withData.toLocaleString() }}</span>
        </div>
        <div class="summary-card summary-card--without">
          <span class="summary-card__label">Sin datos</span>
          <span class="summary-card__value">{{ withoutData.toLocaleString() }}</span>
        </div>
      </div>

      <!-- Agrupación por pedido + línea de pedido (sin card contenedor) -->
      <div class="flex justify-between items-center mb-2 px-1">
        <h2 class="text-sm font-semibold" style="color: rgb(51, 81, 143);">Elementos por pedido y línea</h2>
        <b-button size="is-small" type="is-light" icon-pack="fas" icon-left="rotate"
                  :loading="isLoading" @click="fetchSummary">Actualizar</b-button>
      </div>

      <b-table
        class="ea-table"
        :data="byOrderLine"
        :paginated="byOrderLine.length > perPage"
        :per-page="perPage"
        :narrowed="true"
        :default-sort="['count', 'desc']"
        icon-pack="fas"
      >
        <b-table-column field="purchase_order_id" label="Pedido" sortable searchable v-slot="props">
          {{ props.row.purchase_order_id || '—' }}
        </b-table-column>
        <b-table-column field="order_line_id" label="Línea de pedido" sortable searchable v-slot="props">
          {{ props.row.order_line_id || '—' }}
        </b-table-column>
        <b-table-column field="count" label="Elementos" numeric sortable v-slot="props">
          {{ props.row.count.toLocaleString() }}
        </b-table-column>

        <template #empty>
          <div class="has-text-centered py-6 text-sm" style="color: rgb(51, 81, 143);">
            No hay elementos con atributos extra importados en esta capa.
          </div>
        </template>

        <template slot="bottom-left">
          <span class="text-xs" style="color: rgb(51, 81, 143);">
            {{ byOrderLine.length }} combinaciones (pedido, línea)
          </span>
        </template>
      </b-table>
    </template>
  </div>
</template>

<script>
import { getExtraAttributesSummary } from '../../services/layers.service';

export default {
  name: 'LayerExtraAttributesSummary',

  props: {
    layer: { type: Object, required: true },
  },

  data() {
    return {
      isLoading: false,
      total: 0,
      withData: 0,
      withoutData: 0,
      byOrderLine: [],
      perPage: 12,
    };
  },

  computed: {
    // Sustantivo del tipo de elemento según la capa (ej. "hincas" para pilings)
    // → "Total hincas" en vez de "Total elementos".
    elementNoun() {
      const map = {
        pilings: 'hincas',
        structures: 'estructuras',
        modules: 'módulos',
      };
      return map[this.layer?.code] || 'elementos';
    },
  },

  mounted() {
    this.fetchSummary();
  },

  methods: {
    async fetchSummary() {
      if (!this.layer?.id) return;
      this.isLoading = true;
      try {
        const res = await getExtraAttributesSummary(this.layer.id);
        const s = (res && res.summary) ? res.summary : {};
        this.total       = s.total || 0;
        this.withData    = s.with_data || 0;
        this.withoutData = s.without_data || 0;
        this.byOrderLine = Array.isArray(s.by_order_line) ? s.by_order_line : [];
      } catch (e) {
        this.$toast?.open?.({
          message: 'No se pudo cargar el resumen de la capa.',
          type: 'is-danger',
          position: 'is-top-right',
        });
      } finally {
        this.isLoading = false;
      }
    },

    // Refresco público — el wrapper lo llama al activar este tab (post-import).
    refresh() {
      this.fetchSummary();
    },
  },
};
</script>

<style scoped>
/* Tarjetas de totales — acento a la izquierda + fondo suave por tipo */
.summary-card {
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-left: 3px solid transparent;
}
.summary-card__label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.85;
}
.summary-card__value {
  font-size: 24px;
  font-weight: 700;
}

/* Total — índigo */
.summary-card--total { background-color: rgb(227, 230, 247); border-left-color: rgb(51, 81, 143); }
.summary-card--total .summary-card__label,
.summary-card--total .summary-card__value { color: rgb(51, 81, 143); }

/* Con datos — verde */
.summary-card--with { background-color: rgb(220, 245, 233); border-left-color: rgb(16, 133, 86); }
.summary-card--with .summary-card__label,
.summary-card--with .summary-card__value { color: rgb(6, 108, 71); }

/* Sin datos — ámbar */
.summary-card--without { background-color: rgb(254, 243, 222); border-left-color: rgb(217, 119, 6); }
.summary-card--without .summary-card__label,
.summary-card--without .summary-card__value { color: rgb(180, 83, 9); }

/* Tabla: SOLO el header lleva color; el cuerpo queda neutro */
.ea-table >>> th {
  background-color: rgb(232, 241, 251) !important;
  color: rgb(51, 81, 143) !important;
  font-weight: 700;
}
</style>
